import { useState, useEffect } from "react";
import { Lock, Unlock, Star, TrendingUp, AlertTriangle, Calendar, Target, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { apiCall } from "../App";

// ── Harmony Stars Display ──
const HarmonyStars = ({ score, stars }) => {
  const fullStars = Math.floor(stars);
  const half = stars - fullStars >= 0.5;
  return (
    <div className="flex items-center gap-2" data-testid="harmony-stars">
      <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className={`w-5 h-5 ${i < fullStars ? "fill-divine-gold text-divine-gold" : half && i === fullStars ? "fill-divine-gold/50 text-divine-gold" : "text-chakra-crown/20"}`} />
        ))}
      </div>
      <span className="text-divine-gold font-heading text-lg">{score?.toFixed(2)}</span>
    </div>
  );
};

// ── Client Journey Progress Bar (for Client Dashboard) ──
export const JourneyProgressBar = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const res = await apiCall("GET", "/journey/progress", null, token);
      if (res.success) setData(res.data);
      setLoading(false);
    };
    fetch();
  }, [token]);

  if (loading) return <div className="animate-pulse h-16 bg-cosmic-light/10 rounded-xl" />;
  if (!data?.enrolled) return null;

  const pct = data.total_triggers > 0 ? Math.round((data.unlocked_trigger_ids.length / data.total_triggers) * 100) : 0;

  return (
    <div className="sacred-card" data-testid="journey-progress-bar">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Badge className="bg-divine-gold/20 text-divine-gold border-divine-gold/30">Week {data.current_week}</Badge>
          <span className="text-chakra-crown text-sm">{data.unlocked_trigger_ids.length}/{data.total_triggers} Triggers Unlocked</span>
        </div>
        <HarmonyStars score={data.harmony_score} stars={data.harmony_stars} />
      </div>
      <div className="w-full bg-cosmic-deep rounded-full h-2.5 overflow-hidden">
        <div className="bg-gradient-to-r from-divine-gold/60 to-divine-gold h-full rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-chakra-crown/40 text-xs mt-1.5">Journey Progress: {pct}%</p>
    </div>
  );
};

// ── Client Intervention Card (pinned on Client Dashboard) ──
export const ClientInterventionCard = ({ token }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const res = await apiCall("GET", "/intervention/my_plan", null, token);
      if (res.success && res.data.plan) setPlan(res.data.plan);
      setLoading(false);
    };
    fetch();
  }, [token]);

  const toggleTask = async (taskId, completed) => {
    const endpoint = completed
      ? `/intervention/${plan.plan_id}/uncomplete_task/${taskId}`
      : `/intervention/${plan.plan_id}/complete_task/${taskId}`;
    const res = await apiCall("POST", endpoint, null, token);
    if (res.success) {
      const today = new Date().toISOString().slice(0, 10);
      setPlan(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => t.task_id === taskId ? {
          ...t,
          completed_dates: completed ? t.completed_dates.filter(d => d !== today) : [...t.completed_dates, today]
        } : t)
      }));
    }
  };

  if (loading || !plan) return null;
  const today = new Date().toISOString().slice(0, 10);
  const completedToday = plan.tasks.filter(t => t.completed_dates.includes(today)).length;
  const totalTasks = plan.tasks.length;

  return (
    <div className="sacred-card border-l-4 border-l-divine-gold" data-testid="client-intervention-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-divine-gold font-heading text-base">{plan.title}</h3>
          <p className="text-chakra-crown/50 text-xs">{plan.focus_area} | {plan.duration_weeks} weeks | {plan.check_in_schedule}</p>
        </div>
        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>
      </div>
      {plan.goal && <p className="text-chakra-crown/70 text-sm mb-3"><Target className="w-3.5 h-3.5 inline mr-1" />Goal: {plan.goal}</p>}
      <div className="space-y-2">
        {plan.tasks.map((task) => {
          const done = task.completed_dates.includes(today);
          return (
            <label key={task.task_id} className="flex items-start gap-2 cursor-pointer group" data-testid={`intervention-task-${task.task_id}`}>
              <input type="checkbox" checked={done} onChange={() => toggleTask(task.task_id, done)}
                className="mt-0.5 w-4 h-4 rounded border-divine-gold/30 text-divine-gold focus:ring-divine-gold/50" />
              <span className={`text-sm ${done ? "text-chakra-crown/40 line-through" : "text-chakra-crown"}`}>
                {task.description}
                {task.type === "prescribed" && <Badge variant="outline" className="ml-2 text-[10px] border-divine-gold/20 text-divine-gold/50">Trigger</Badge>}
              </span>
            </label>
          );
        })}
      </div>
      <div className="mt-3 pt-3 border-t border-divine-gold/10 flex items-center justify-between">
        <span className="text-chakra-crown/50 text-xs">Today: {completedToday}/{totalTasks} tasks</span>
        <div className="w-24 bg-cosmic-deep rounded-full h-1.5">
          <div className="bg-divine-gold h-full rounded-full" style={{ width: `${(completedToday / totalTasks) * 100}%` }} />
        </div>
      </div>
    </div>
  );
};

// ── Coach: Client Journey View ──
export const CoachJourneyView = ({ userId, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlockIds, setUnlockIds] = useState("");

  useEffect(() => {
    const fetch = async () => {
      const res = await apiCall("GET", `/journey/client/${userId}/progress`, null, token);
      if (res.success) setData(res.data);
      setLoading(false);
    };
    if (userId) fetch();
  }, [userId, token]);

  const handleForceUnlock = async (action) => {
    const ids = unlockIds.split(",").map(s => parseInt(s.trim())).filter(n => !isNaN(n));
    if (!ids.length) { toast.error("Enter trigger IDs (comma-separated)"); return; }
    const res = await apiCall("POST", `/journey/force_unlock/${userId}`, { trigger_ids: ids, action }, token);
    if (res.success) {
      toast.success(res.data.message);
      setUnlockIds("");
      // Refresh
      const r2 = await apiCall("GET", `/journey/client/${userId}/progress`, null, token);
      if (r2.success) setData(r2.data);
    } else { toast.error(res.error || "Failed"); }
  };

  if (loading) return <div className="animate-pulse h-20 bg-cosmic-light/10 rounded-xl" />;
  if (!data?.enrolled) return <p className="text-chakra-crown/50 text-sm py-4">Client not yet enrolled in journey</p>;

  const pct = data.total_triggers > 0 ? Math.round((data.unlocked_trigger_ids.length / data.total_triggers) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="coach-journey-view">
      {/* Progress Overview */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge className="bg-divine-gold/20 text-divine-gold border-divine-gold/30">Week {data.current_week}</Badge>
          <span className="text-chakra-crown text-sm">{data.unlocked_trigger_ids.length}/{data.total_triggers} Triggers</span>
        </div>
        <HarmonyStars score={data.harmony_score} stars={data.harmony_stars} />
      </div>
      <div className="w-full bg-cosmic-deep rounded-full h-2.5 overflow-hidden">
        <div className="bg-gradient-to-r from-divine-gold/60 to-divine-gold h-full rounded-full" style={{ width: `${pct}%` }} />
      </div>

      {/* Red Flags */}
      {data.red_flags?.length > 0 && (
        <div className="space-y-2" data-testid="red-flags">
          {data.red_flags.map((flag, i) => (
            <div key={i} className={`p-2.5 rounded-lg flex items-center gap-2 ${flag.type === "emergency" ? "bg-red-500/20 border border-red-500/30" : "bg-amber-500/10 border border-amber-500/20"}`}>
              <AlertTriangle className={`w-4 h-4 ${flag.type === "emergency" ? "text-red-400" : "text-amber-400"}`} />
              <span className={`text-sm ${flag.type === "emergency" ? "text-red-300" : "text-amber-300"}`}>{flag.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Force Unlock */}
      <div className="p-3 rounded-xl bg-cosmic-light/10 border border-divine-gold/10">
        <h4 className="text-divine-gold text-sm font-medium mb-2">Coach Override</h4>
        <div className="flex gap-2">
          <input value={unlockIds} onChange={e => setUnlockIds(e.target.value)} placeholder="Trigger IDs: 45, 67, 89"
            className="flex-1 px-3 py-1.5 rounded-lg bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown text-sm placeholder:text-chakra-crown/30 focus:outline-none focus:border-divine-gold/50"
            data-testid="force-unlock-input" />
          <Button size="sm" onClick={() => handleForceUnlock("unlock")} className="bg-divine-gold text-cosmic-void text-xs" data-testid="force-unlock-btn">
            <Unlock className="w-3 h-3 mr-1" />Unlock
          </Button>
          <Button size="sm" onClick={() => handleForceUnlock("lock")} variant="outline" className="border-chakra-root/30 text-chakra-root text-xs hover:bg-chakra-root/10" data-testid="force-lock-btn">
            <Lock className="w-3 h-3 mr-1" />Lock
          </Button>
        </div>
      </div>

      {/* Weekly Scores */}
      {data.weekly_scores?.length > 0 && (
        <div>
          <h4 className="text-chakra-crown/60 text-xs font-medium mb-2">WEEKLY HISTORY</h4>
          <div className="grid grid-cols-3 gap-2 max-h-[150px] overflow-y-auto">
            {data.weekly_scores.map((w, i) => (
              <div key={i} className="p-2 rounded bg-cosmic-deep text-center">
                <p className="text-divine-gold/80 text-xs font-medium">W{w.week}</p>
                <p className="text-chakra-crown text-xs">{w.compliance_pct}%</p>
                <p className="text-chakra-crown/40 text-[10px]">{w.triggers_unlocked} new</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Coach: Intervention Plan Manager ──
export const InterventionPlanManager = ({ userId, clientName, token, triggerBoard }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    title: "", focus_area: "trinity_gap", duration_weeks: 4,
    prescribed_trigger_ids: "", custom_tasks: "", check_in_schedule: "weekly", goal: ""
  });

  useEffect(() => {
    const fetch = async () => {
      const res = await apiCall("GET", `/intervention/client/${userId}`, null, token);
      if (res.success) setPlans(res.data.plans || []);
      setLoading(false);
    };
    if (userId) fetch();
  }, [userId, token]);

  const handleCreate = async () => {
    const triggerIds = form.prescribed_trigger_ids.split(",").map(s => parseInt(s.trim())).filter(n => !isNaN(n));
    const tasks = form.custom_tasks.split("\n").filter(s => s.trim());
    const res = await apiCall("POST", `/intervention/create/${userId}`, {
      ...form, prescribed_trigger_ids: triggerIds, custom_tasks: tasks, duration_weeks: parseInt(form.duration_weeks)
    }, token);
    if (res.success) {
      setPlans(prev => [res.data, ...prev]);
      setShowCreate(false);
      setForm({ title: "", focus_area: "trinity_gap", duration_weeks: 4, prescribed_trigger_ids: "", custom_tasks: "", check_in_schedule: "weekly", goal: "" });
      toast.success("Intervention plan created. Prescribed triggers auto-unlocked.");
    } else { toast.error(res.error || "Failed"); }
  };

  const handleStatusChange = async (planId, status) => {
    const res = await apiCall("PUT", `/intervention/${planId}`, { status }, token);
    if (res.success) {
      setPlans(prev => prev.map(p => p.plan_id === planId ? { ...p, status } : p));
      toast.success(`Plan ${status}`);
    }
  };

  const focusAreas = [
    { value: "trinity_gap", label: "Trinity Gap" }, { value: "rajas", label: "Rajas Reduction" },
    { value: "tamas", label: "Tamas Reduction" }, { value: "vikara_kama", label: "Vikara: Kama" },
    { value: "vikara_krodha", label: "Vikara: Krodha" }, { value: "vikara_lobha", label: "Vikara: Lobha" },
    { value: "energy_balance", label: "Energy Balance" },
  ];

  if (loading) return <div className="animate-pulse h-20 bg-cosmic-light/10 rounded-xl" />;

  const active = plans.find(p => p.status === "active");

  return (
    <div className="space-y-4" data-testid="intervention-manager">
      {!showCreate && (
        <Button size="sm" onClick={() => setShowCreate(true)} className="bg-divine-gold text-cosmic-void" data-testid="create-intervention-btn">
          <Target className="w-4 h-4 mr-1" />Create Intervention Plan
        </Button>
      )}

      {showCreate && (
        <div className="p-4 rounded-xl bg-cosmic-light/10 border border-divine-gold/20 space-y-3" data-testid="intervention-form">
          <input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Plan Title (e.g., Tamas Reduction Phase 1)"
            className="w-full px-3 py-2 rounded-lg bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown text-sm placeholder:text-chakra-crown/30 focus:outline-none focus:border-divine-gold/50"
            data-testid="intervention-title" />
          <div className="grid grid-cols-3 gap-2">
            <select value={form.focus_area} onChange={e => setForm(p => ({...p, focus_area: e.target.value}))}
              className="px-3 py-2 rounded-lg bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown text-sm" data-testid="intervention-focus">
              {focusAreas.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <select value={form.duration_weeks} onChange={e => setForm(p => ({...p, duration_weeks: e.target.value}))}
              className="px-3 py-2 rounded-lg bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown text-sm" data-testid="intervention-duration">
              <option value={2}>2 weeks</option><option value={4}>4 weeks</option><option value={8}>8 weeks</option>
            </select>
            <select value={form.check_in_schedule} onChange={e => setForm(p => ({...p, check_in_schedule: e.target.value}))}
              className="px-3 py-2 rounded-lg bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown text-sm" data-testid="intervention-checkin">
              <option value="weekly">Weekly</option><option value="biweekly">Bi-weekly</option>
            </select>
          </div>
          <input value={form.prescribed_trigger_ids} onChange={e => setForm(p => ({...p, prescribed_trigger_ids: e.target.value}))} placeholder="Prescribed Trigger IDs (comma-separated): 45, 67, 89"
            className="w-full px-3 py-2 rounded-lg bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown text-sm placeholder:text-chakra-crown/30 focus:outline-none focus:border-divine-gold/50"
            data-testid="intervention-triggers" />
          <textarea value={form.custom_tasks} onChange={e => setForm(p => ({...p, custom_tasks: e.target.value}))} placeholder="Custom Tasks (one per line):"
            className="w-full min-h-[60px] px-3 py-2 rounded-lg bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown text-sm placeholder:text-chakra-crown/30 resize-y focus:outline-none focus:border-divine-gold/50"
            data-testid="intervention-tasks" />
          <input value={form.goal} onChange={e => setForm(p => ({...p, goal: e.target.value}))} placeholder="Goal: Reduce Tamas from 33% to 25%"
            className="w-full px-3 py-2 rounded-lg bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown text-sm placeholder:text-chakra-crown/30 focus:outline-none focus:border-divine-gold/50"
            data-testid="intervention-goal" />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreate} disabled={!form.title.trim()} className="bg-divine-gold text-cosmic-void" data-testid="save-intervention-btn">Create Plan</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)} className="text-chakra-crown/60">Cancel</Button>
          </div>
        </div>
      )}

      {/* Active Plan */}
      {active && (
        <div className="p-4 rounded-xl bg-cosmic-light/10 border-l-4 border-l-green-500 border border-divine-gold/10" data-testid="active-intervention">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h4 className="text-divine-gold font-medium">{active.title}</h4>
              <p className="text-chakra-crown/50 text-xs">{active.focus_area} | {active.duration_weeks}wk | {active.check_in_schedule}</p>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => handleStatusChange(active.plan_id, "completed")} className="text-green-400 text-xs hover:bg-green-500/10" data-testid="complete-plan-btn">Complete</Button>
              <Button size="sm" variant="ghost" onClick={() => handleStatusChange(active.plan_id, "paused")} className="text-amber-400 text-xs hover:bg-amber-500/10" data-testid="pause-plan-btn">Pause</Button>
            </div>
          </div>
          {active.goal && <p className="text-chakra-crown/70 text-sm mb-2"><Target className="w-3 h-3 inline mr-1" />{active.goal}</p>}
          <div className="space-y-1.5">
            {active.tasks?.map(t => {
              const today = new Date().toISOString().slice(0, 10);
              const done = t.completed_dates?.includes(today);
              return (
                <div key={t.task_id} className="flex items-center gap-2 text-sm">
                  <div className={`w-3 h-3 rounded-full ${done ? "bg-green-500" : "bg-cosmic-deep border border-divine-gold/20"}`} />
                  <span className={done ? "text-chakra-crown/40 line-through" : "text-chakra-crown"}>{t.description}</span>
                  {t.type === "prescribed" && <Badge variant="outline" className="text-[10px] border-divine-gold/20 text-divine-gold/40">T#{t.trigger_id}</Badge>}
                  <span className="text-chakra-crown/30 text-xs ml-auto">{t.completed_dates?.length || 0}d</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Past Plans */}
      {plans.filter(p => p.status !== "active").length > 0 && (
        <div>
          <h4 className="text-chakra-crown/50 text-xs font-medium mb-2">PAST PLANS</h4>
          <div className="space-y-2">
            {plans.filter(p => p.status !== "active").map(p => (
              <div key={p.plan_id} className="p-3 rounded-lg bg-cosmic-deep/50 flex items-center justify-between">
                <div>
                  <span className="text-chakra-crown/70 text-sm">{p.title}</span>
                  <span className="text-chakra-crown/40 text-xs ml-2">{p.focus_area} | {p.duration_weeks}wk</span>
                </div>
                <Badge variant="outline" className={`text-xs capitalize ${p.status === "completed" ? "border-green-500/30 text-green-400" : "border-amber-500/30 text-amber-400"}`}>{p.status}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Admin: Escalation Summary ──
export const EscalationPanel = ({ token }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const res = await apiCall("GET", "/journey/escalations", null, token);
      if (res.success) setData(res.data.escalations || []);
      setLoading(false);
    };
    fetch();
  }, [token]);

  if (loading) return <div className="animate-pulse h-12 bg-cosmic-light/10 rounded-xl" />;
  if (!data.length) return null;

  return (
    <div className="sacred-card border-l-4 border-l-red-500" data-testid="escalation-panel">
      <h3 className="font-heading text-lg text-red-400 mb-3 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" /> ESCALATIONS ({data.length})
      </h3>
      <div className="space-y-2">
        {data.map((e, i) => (
          <div key={i} className="p-3 rounded-lg bg-cosmic-deep/50 border border-red-500/10">
            <div className="flex items-center justify-between mb-1">
              <span className="text-chakra-crown font-medium text-sm">{e.client_name}</span>
              <span className="text-chakra-crown/40 text-xs">W{e.current_week} | H:{e.harmony_score}</span>
            </div>
            {e.flags.map((f, fi) => (
              <div key={fi} className={`text-xs px-2 py-1 rounded mt-1 ${f.severity === "critical" ? "bg-red-500/20 text-red-300" : "bg-amber-500/10 text-amber-300"}`}>
                {f.message}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
